import {
  Description,
  EventNote,
  EventNoteOutlined,
  Folder,
  Functions,
  Restore,
  Search,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  createSvgIcon,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import * as dayjs from "dayjs";
import AlertModal from "../../../componentes/alertModal";
import api from "../../../services/api";
import { ptBR } from "@mui/x-data-grid/locales";
import { useStyle } from "./styles";
import { ReactComponent as PerfilReportSvg } from "../../../assets/easyReport/iconSvg/perfilReport.svg";
import LixeiraFilter from "../../../componentes/easyReport/lixeiraFilter";
import { ReactComponent as EmptyBox } from "../../../assets/easyReport/iconSvg/emptyBox.svg";
import { useNotification } from "../../../componentes/notificationProvider";

const TYPES = ["Relatório", "Função", "Perfil", "Agendamento", "Grupo"];

export default function Lixeira() {
  const [lixeiraData, setLixeiraData] = useState<LixeiraDataType[]>([]);
  const [restoreModal, setRestoreModal] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [deletedAt, setDeletedAt] = useState<dayjs.Dayjs | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [pageSize, setPageSize] = useState<number>(10);

  const [restoreRow, setRestoreRow] = useState<LixeiraDataType | null>(null);
  const [loading, setLoading] = useState(false);

  const PerfilReport = createSvgIcon(<PerfilReportSvg />, "PerfilReport");

  const theme = useTheme();
  const styles = useStyle(theme);
  const notify = useNotification();

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);

        const res = await api.get("/easyReport/v2/findDeleted");
        setLixeiraData(res.data);
      } catch (error) {
        console.error("Erro ao buscar relatórios:", error);
        notify("Erro ao carregar lixeira.", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, []);

  const columns: GridColDef<LixeiraDataType>[] = [
    {
      field: "type",
      headerName: "Tipo",
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams<LixeiraDataType, string>) => {
        switch (params.value) {
          case "Função":
            return (
              <Box sx={styles.typeDatagrid}>
                <Functions />
                <Typography>Função</Typography>
              </Box>
            );
          case "Relatório":
            return (
              <Box sx={styles.typeDatagrid}>
                <Description />
                <Typography>Relatório</Typography>
              </Box>
            );
          case "Perfil":
            return (
              <Box sx={styles.typeDatagrid}>
                <PerfilReport />
                <Typography>Perfil</Typography>
              </Box>
            );
          case "Agendamento":
            return (
              <Box sx={styles.typeDatagrid}>
                <EventNote />
                <Typography>Agendamento</Typography>
              </Box>
            );
          case "Grupo":
            return (
              <Box sx={styles.typeDatagrid}>
                <Folder />
                <Typography>Grupo</Typography>
              </Box>
            );
          default:
            return <Chip label={params.value || "-"} />;
        }
      },
    },
    {
      field: "title",
      headerName: "Título",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "deletedAt",
      headerName: "Excluído em",
      width: 180,
      renderCell: (params: GridRenderCellParams<LixeiraDataType, string>) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography sx={styles.typography}>
            {params.row.deletedAt
              ? dayjs(params.row.deletedAt).format("DD/MM/YYYY")
              : "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "permanentRemovalAt",
      headerName: "Será excluído em",
      width: 150,
      renderCell: (params: GridRenderCellParams<LixeiraDataType, string>) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography sx={styles.typography}>
            {params.row.permanentRemovalAt
              ? (() => {
                  const now = dayjs();
                  const removalDate = dayjs(params.row.permanentRemovalAt);
                  const diff = removalDate.diff(now, "day");
                  if (diff > 1) return `${diff} dias`;
                  if (diff === 1) return "1 dia";
                  if (diff === 0) return "Hoje";
                  return "Expirado";
                })()
              : "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "restore",
      headerName: "Ações",
      width: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params: GridRenderCellParams<LixeiraDataType>) => {
        const handleOpenModal = (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          setRestoreModal(true);
          setRestoreRow(params.row);
        };
        return (
          <Tooltip title="Restaurar">
            <IconButton color="primary" onClick={handleOpenModal}>
              <Restore />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  const handleRestoreModal = () => {
    setRestoreModal(false);
    if (restoreRow) {
      handleRestore(restoreRow.id, restoreRow.type);
      setRestoreRow(null);
    }
  };

  const handleRestore = async (id: string, type: string) => {
    try {
      setLoading(true);
      await api.post("/easyReport/v2/restoreDeleted", { id, type });
      setLixeiraData((prev) => prev.filter((item) => item.id !== id));
      notify("Item restaurado com sucesso!", "success");
    } catch (error) {
      console.error("Erro ao restaurar item:", error);
      notify("Erro ao restaurar item.", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    return lixeiraData.filter((row) => {
      const search = searchValue.toLowerCase();
      const matchesSearch =
        (row.title && row.title.toLowerCase().includes(search)) ||
        (row.type && row.type.toLowerCase().includes(search));

      const matchesType =
        selectedTypes.length === 0 || selectedTypes.includes(row.type);

      const matchesDeletedAt =
        !deletedAt ||
        (row.deletedAt && dayjs(row.deletedAt).isSame(deletedAt, "day"));

      return matchesSearch && matchesType && matchesDeletedAt;
    });
  }, [searchValue, lixeiraData, selectedTypes, deletedAt]);

  function CustomNoRowsOverlay() {
    return (
      <Box sx={styles.boxNoRowsOverlay}>
        <EmptyBox style={styles.iconUserNotFound} />
        <Typography color="text.disabled">
          Nenhum item na lixeira até o momento
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={styles.container}>
      <Typography variant="h6">Lixeira</Typography>
      <Paper sx={styles.paper}>
        <Box sx={styles.boxHeader}>
          <Box sx={styles.boxSearchAndFilter}>
            <TextField
              size="small"
              value={searchValue}
              placeholder="Pesquisar por relatório, agendamento, função ou perfil"
              onChange={(e) => setSearchValue(e.target.value)}
              sx={styles.searchInput}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <LixeiraFilter
              types={TYPES}
              selectedTypes={selectedTypes}
              setSelectedTypes={setSelectedTypes}
              deletedAt={deletedAt}
              setDeletedAt={setDeletedAt}
            />
          </Box>
        </Box>
        <Alert severity="warning">
          Os itens que estiverem na Lixeira serão excluídos automaticamente
          depois de 30 dias.
        </Alert>
        <Box
          sx={{
            width: "100%",
            height: "65vh",
            overflow: "hidden",
            borderRadius: 2,
            border: `1px solid ${theme.palette.stroke}`,
          }}
        >
          <DataGrid
            sx={styles.dataGrid}
            rows={filteredRows}
            columns={columns}
            loading={loading}
            disableColumnMenu={true}
            pageSizeOptions={[10, 25, 50, 100]}
            onPaginationModelChange={(model) => setPageSize(model.pageSize)}
            disableRowSelectionOnClick
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0
                ? "even-row"
                : "odd-row"
            }
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: pageSize },
              },
            }}
          />
        </Box>
        <AlertModal
          openModal={restoreModal}
          setOpenModal={setRestoreModal}
          severity="info"
          singleButton={false}
          handleButton={handleRestoreModal}
          handleCancel={() => {
            setRestoreModal(false);
          }}
          textContent="Tem certeza que deseja restaurar o item?"
          buttonText="Restaurar"
          buttonCancelText="Cancelar"
        />
      </Paper>
    </Box>
  );
}
